import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsis,
    faPenToSquare,
    faCircleMinus,
    faPlus,
} from "@fortawesome/pro-regular-svg-icons";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import TdLink from "../../components/TdLink";
import CustomDropdown from "../../components/CustomDropdown";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingSpinner from "../../components/LoadingSpinner";
import DataTable from "../../components/DataTable";

import toast from "react-hot-toast";
import { useAuth } from "../../providers/AuthContext";

import {
    getAllOrganizations,
    deleteOrganization,
} from "../../api/Resources"; 
import { useTranslation } from "react-i18next";

const OrganizationsPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();

    const [orgs, setOrgs] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const [search, setSearch] = useState("");

    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: "",
        confirm: () => { },
    });

    useEffect(() => {
        handleGetOrgs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGetOrgs = async () => {
        setLoaded(false);
        try {
            const token = await getAccessToken();
            const res = await getAllOrganizations(token);

            const mapped = (Array.isArray(res) ? res : []).map((o) => ({
                ...o,
                organizationName: o?.organizationName ?? o?.name ?? "",
                organizationPublicId: o?.organizationPublicId ?? o?.publicId ?? "",
            }));

            setOrgs(mapped);
        } catch (err) {
            console.error(err);
            toast.error(err?.message || t("toast.failedFetch", { entity: t("entity.organizations") }));
            setOrgs([]);
        } finally {
            setLoaded(true);
        }
    };

    const handleDeleteOrg = async (organizationPublicId) => {
        const toastId = toast.loading(t("toast.deleting", { entity: t("entity.organization") }));
        try {
            const token = await getAccessToken();
            await deleteOrganization(organizationPublicId, token);

            setOrgs((prev) =>
                prev.filter((o) => o.organizationPublicId !== organizationPublicId)
            );

            toast.success(t("toast.deleted", { entity: t("entity.organization") }), { id: toastId });
        } catch (err) {
            console.error(err);
            toast.error(t("toast.failedDelete", { entity: t("entity.organization") }), { id: toastId });
        }
    };

    const columns = [
        {
            key: "organizationPublicId",
            header: t("common.publicId"),
            sortable: true,
            rawCell: true,
            getSortValue: (org) => org.organizationPublicId || "",
            render: (org) => (
                <TdLink to={`/organization/${org.organizationPublicId}`}>
                    {org.organizationPublicId || ""}
                </TdLink>
            ),
        },
        {
            key: "organizationName",
            header: t("table.organizationName"),
            sortable: true,
            rawCell: true,
            getSortValue: (org) => org.organizationName || "",
            render: (org) => (
                <TdLink to={`/organization/${org.organizationPublicId}`}>
                    {org.organizationName || ""}
                </TdLink>
            ),
        },
        {
            key: "actions",
            header: "",
            sortable: false,
            searchable: false,
            rawCell: true,
            render: (org) => (
                <td>
                    <div className="flex justify-end">
                        <CustomDropdown
                            isRight={true}
                            ContentComponent={() => (
                                <div className="w-44 flex flex-col items-start bg-white p-2 rounded-lg">
                                    <NavLink
                                        className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                        to={`/organization/${org.organizationPublicId}`}
                                    >
                                        <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                                    </NavLink>

                                    <button
                                        className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                        onClick={() =>
                                            setConfirmState({
                                                isOpen: true,
                                                title: t("confirm.deleteEntity", {
                                                    entity: t("entity.organization"),
                                                    name: org.organizationName,
                                                }),
                                                confirm: () =>
                                                    handleDeleteOrg(org.organizationPublicId),
                                            })
                                        }
                                    >
                                        <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                    </button>
                                </div>
                            )}
                        >
                            <div className="w-8 h-6 flex justify-end items-center cursor-pointer">
                                <FontAwesomeIcon icon={faEllipsis} />
                            </div>
                        </CustomDropdown>
                    </div>
                </td>
            ),
        },
    ];

    if (!loaded) {
        return (
            <>
                <HeaderBar />
                <div className="flex">
                    <Sidebar />
                    <main className="page flex items-center justify-center">
                        <LoadingSpinner text={t("common.loadingEntity", { name: t("entity.organizations") })} />
                    </main>
                </div>
            </>
        );
    }

    return (
        <>
            <HeaderBar />
            <div className="flex flex-col">
                <div className="flex flex-row">
                    <Sidebar />

                    <main className="page">
                        <div className="page-content">
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl text-black font-bold pb-2">
                                    {t("entity.organizations")}
                                </h2>

                                <NavLink className="btn btn-primary" to={`/organization/create`}>
                                    <FontAwesomeIcon icon={faPlus} /> {t("pages.createOrganization")}
                                </NavLink>
                            </div>

                            <div className="rounded-md shadow-md w-full p-4">
                                {/* Table */}
                                <DataTable
                                    rows={orgs}
                                    columns={columns}
                                    search={search}
                                    onSearchChange={setSearch}
                                    searchPlaceholder={t("search.byNameNumberOrId")}
                                    rowKey={(org, index) => org.organizationPublicId || index}
                                    emptyColSpan={3}
                                    emptyContent={t("common.noEntityFound", { entity: t("entity.organizations") })}
                                />
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <ConfirmationModal
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
                onConfirm={() => {
                    setConfirmState((s) => ({ ...s, isOpen: false }));
                    confirmState.confirm();
                }}
            />
        </>
    );
};

export default OrganizationsPage;
